import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { UserRound, Wrench, Building2, Users, Briefcase, FolderKanban, Check, Eye, EyeOff, Mail, ShieldCheck, Bell, AlertCircle, ArrowLeft } from "lucide-react";
import { Logo } from "../lib/brand";
import { activities } from "../data/mock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const icons: Record<string, any> = { UserRound, Wrench, Building2, Users, Briefcase, FolderKanban };
// map prototype activity keys → server activity keys
const KEY_MAP: Record<string, string> = {
  "find-work": "find_work", "offer-service": "offer_service", "promote-company": "promote_company",
  "hire": "hire", "post-job": "post_job", "post-project": "post_project",
};

export default function Signup() {
  const [picked, setPicked] = useState<string[]>([]);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", confirmPassword: "" });
  const [error, setError] = useState<string | null>(null);
  const [activityError, setActivityError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, navigate] = useLocation();
  const { user, setUser } = useAuth();
  const activityRef = useRef<HTMLDivElement>(null);
  const toggle = (k: string) => {
    setActivityError(false);
    setPicked((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  };

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  // QA #29: password strength (0–4) from length + character-class variety.
  const pwStrength = (pw: string) => {
    let s = 0;
    if (pw.length >= 8) s++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
    if (/\d/.test(pw)) s++;
    if (/[^A-Za-z0-9]/.test(pw)) s++;
    return s;
  };
  const STRENGTH = [
    { label: "Too weak", color: "bg-brand-red", text: "text-brand-red" },
    { label: "Weak", color: "bg-brand-red", text: "text-brand-red" },
    { label: "Fair", color: "bg-brand-gold", text: "text-brand-gold" },
    { label: "Good", color: "bg-brand-gold", text: "text-brand-gold" },
    { label: "Strong", color: "bg-brand-green", text: "text-brand-green" },
  ];
  const score = pwStrength(form.password);

  // Step 1: validate the form + activity selection, then open the email-confirmation modal.
  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    // QA #29: enforce a minimum password and matching confirmation before continuing.
    if (form.password.length < 8) { setError("Your password must be at least 8 characters."); return; }
    if (form.password !== form.confirmPassword) { setError("Passwords do not match. Please re-enter them."); return; }
    const acts = picked.map((k) => KEY_MAP[k]).filter(Boolean);
    if (acts.length === 0) {
      setActivityError(true);
      setError("Please select at least one activity before registering.");
      activityRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setActivityError(false);
    setConfirmOpen(true);
  }

  // Step 2: user confirmed their email — actually create the account.
  async function doRegister() {
    const acts = picked.map((k) => KEY_MAP[k]).filter(Boolean);
    if (acts.length === 0) { setConfirmOpen(false); setActivityError(true); return; }
    setBusy(true);
    try {
      const { confirmPassword: _cp, ...payload } = form;
      const u = await api.signup({ ...payload, activities: acts });
      if (u) {
        // v3.5.0: flag a fresh registration so the dashboard shows the onboarding guide once.
        try { localStorage.setItem("kph_onboarding_pending", "1"); } catch { /* ignore */ }
        setUser(u);
      } else { setError("Could not create the account."); setConfirmOpen(false); }
    } catch (err: any) {
      const msg = err?.message || "Signup failed.";
      setConfirmOpen(false);
      // #4: if the email is already registered, send them to sign in instead of a dead end.
      if (/already exists|already in use|already registered/i.test(msg)) {
        setError("An account with this email already exists — taking you to sign in…");
        setTimeout(() => navigate("/login"), 1400);
      } else setError(msg);
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-[80vh] container-x py-10 grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <Logo withTagline />
        <h1 className="text-3xl font-extrabold text-navy mt-6">What would you like to do on KontrataPH?</h1>
        <p className="text-mut mt-2">One account, multiple activities. Pick any or all — you can switch anytime, and you'll never be locked into a single role.</p>
        <p className="text-sm text-navy-600 mt-2 bg-navy-50 rounded-lg p-3">
          <b>Want to work?</b> Choose <b>Find Work</b> or <b>Offer a Service</b>. <b>Want to hire?</b> Choose <b>Hire</b>, <b>Post a Job</b>, or <b>Post a Project</b>. Select at least one to continue.
        </p>

        <div
          ref={activityRef}
          className={`rounded-xl mt-6 transition ${activityError ? "ring-2 ring-brand-red bg-brand-red/5 p-3" : ""}`}
        >
          {activityError && (
            <p className="flex items-center gap-2 text-sm font-semibold text-brand-red mb-3">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Select at least one activity to continue.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-2.5">
            {activities.map((a) => {
              const I = icons[a.icon]; const on = picked.includes(a.key);
              return (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => toggle(a.key)}
                  className={`p-4 rounded-lg border text-left flex items-start gap-3 transition ${
                    on ? "border-navy-500 bg-navy-50 ring-2 ring-navy-50"
                       : activityError ? "border-brand-red/50 hover:border-brand-red"
                       : "border-line hover:border-navy-500"
                  }`}
                >
                  <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${on ? "bg-navy text-white" : "bg-navy-50 text-navy"}`}>{on ? <Check className="w-5 h-5" /> : <I className="w-5 h-5" />}</div>
                  <div><div className="font-semibold text-navy text-sm">{a.title}</div><div className="text-xs text-mut">{a.sub}</div></div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card p-6 sm:p-8 max-w-md w-full mx-auto">
        <h2 className="text-xl font-extrabold text-navy mb-1">Create your free account</h2>
        <p className="text-sm text-mut mb-5">Selected: {picked.length} activit{picked.length === 1 ? "y" : "ies"}</p>
        <form className="space-y-3" onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">First name</label><input required className="field" placeholder="Juan" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></div>
            <div><label className="label">Last name</label><input required className="field" placeholder="Dela Cruz" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></div>
          </div>
          <div><label className="label">Email</label><input required type="email" className="field" placeholder="you@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div>
            <label className="label">Password</label>
            <div className="relative">
              <input required type={showPw ? "text" : "password"} minLength={8} className="field pr-11" placeholder="Create a password (min 8 chars)" value={form.password} onChange={(e) => { setForm({ ...form, password: e.target.value }); if (error) setError(null); }} />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 px-3 flex items-center text-mut hover:text-navy">
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {/* QA #29: live strength meter */}
            {form.password && (
              <div className="mt-1.5">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <span key={i} className={`h-1.5 flex-1 rounded-full ${i < score ? STRENGTH[score].color : "bg-line"}`} />
                  ))}
                </div>
                <p className={`text-[11px] mt-1 ${STRENGTH[score].text}`}>Password strength: {STRENGTH[score].label} · use 8+ chars with upper &amp; lower case, a number, and a symbol.</p>
              </div>
            )}
          </div>
          <div>
            <label className="label">Confirm password</label>
            <div className="relative">
              <input required type={showPw2 ? "text" : "password"} minLength={8} className="field pr-11" placeholder="Re-enter your password" value={form.confirmPassword} onChange={(e) => { setForm({ ...form, confirmPassword: e.target.value }); if (error) setError(null); }} />
              <button type="button" onClick={() => setShowPw2((v) => !v)} aria-label={showPw2 ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 px-3 flex items-center text-mut hover:text-navy">
                {showPw2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {form.confirmPassword && form.confirmPassword !== form.password && (
              <p className="text-[11px] mt-1 text-brand-red flex items-center gap-1"><AlertCircle className="w-3 h-3" />Passwords don't match yet.</p>
            )}
          </div>
          <label className="flex items-start gap-2 text-xs text-mut"><input type="checkbox" required className="mt-0.5 shrink-0" /><span className="text-left leading-relaxed">I agree to the <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-brand-red hover:underline">Terms of Service</a> and <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-brand-red hover:underline">Privacy Policy</a>.</span></label>
          {error && <p className="text-sm text-brand-red flex items-center gap-1.5"><AlertCircle className="w-4 h-4 shrink-0" />{error}</p>}
          <button type="submit" disabled={busy} className="btn-red w-full disabled:opacity-60">{busy ? "Creating account…" : "Join KontrataPH — Free"}</button>
          <p className="text-center text-sm text-mut">Already have an account? <Link href="/login" className="text-brand-red font-semibold">Sign In</Link></p>
        </form>
      </div>

      {/* Email confirmation modal — shown after the form validates, before the account is created. */}
      {confirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy/60 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* graphic header */}
            <div className="bg-navy px-6 pt-7 pb-6 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-white/10 grid place-items-center">
                <div className="w-12 h-12 rounded-full bg-brand-gold grid place-items-center">
                  <Mail className="w-6 h-6 text-navy" />
                </div>
              </div>
              <div className="h-1 w-full bg-brand-gold mt-6 -mb-6" />
            </div>
            <div className="px-6 sm:px-7 py-6">
              <h3 className="text-xl font-extrabold text-navy text-center">Confirm your email address</h3>
              <p className="text-sm text-mut text-center mt-2">You're about to create your KontrataPH account with:</p>
              <div className="mt-3 mb-4 rounded-lg border border-line bg-paper px-4 py-3 text-center">
                <span className="font-semibold text-navy break-all">{form.email || "—"}</span>
              </div>
              <div className="space-y-2.5">
                <p className="flex items-start gap-2.5 text-sm text-ink">
                  <ShieldCheck className="w-5 h-5 text-brand-green shrink-0" />
                  <span>Make sure this email is <b>active and correct</b> — we'll send a verification link to activate your account.</span>
                </p>
                <p className="flex items-start gap-2.5 text-sm text-ink">
                  <Bell className="w-5 h-5 text-brand-gold shrink-0" />
                  <span>We'll also use it for important notifications about your jobs, bids, orders and payments.</span>
                </p>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setConfirmOpen(false)} disabled={busy} className="btn-outline flex-1 justify-center">
                  <ArrowLeft className="w-4 h-4" />Back
                </button>
                <button type="button" onClick={doRegister} disabled={busy} className="btn-red flex-1 justify-center disabled:opacity-60">
                  {busy ? "Creating…" : <><Check className="w-4 h-4" />Confirm & register</>}
                </button>
              </div>
              <p className="text-center text-[11px] text-mut mt-3">Wrong email? Tap Back to edit it before continuing.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
